import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Shared "focus mode" state: any page can toggle it via usePageFocusMode()/<PageFocusToggle />,
// and AppLayout is the sole consumer that actually hides the sidebar/topbar chrome and expands
// page-content to fill the viewport. Kept as app-wide context (not page-local state) so future
// pages can wire in the same reusable toggle with no plumbing beyond importing the hook/component.

interface PageFocusContextValue {
  focusMode: boolean;
  toggleFocusMode: () => void;
  exitFocusMode: () => void;
  enterFocusMode: () => void;
}

const PageFocusContext = createContext<PageFocusContextValue>({
  focusMode: false,
  toggleFocusMode: () => {},
  exitFocusMode: () => {},
  enterFocusMode: () => {},
});

export function PageFocusProvider({ children }: { children: React.ReactNode }) {
  const [focusMode, setFocusMode] = useState(false);
  const location = useLocation();

  const toggleFocusMode = useCallback(() => setFocusMode((f) => !f), []);
  const exitFocusMode = useCallback(() => setFocusMode(false), []);
  // Unconditional (idempotent) activation — for automatic takeovers like the WO detail view,
  // which should always land in focus mode regardless of whatever it was before, unlike
  // toggleFocusMode which would flip it back off if it happened to already be on.
  const enterFocusMode = useCallback(() => setFocusMode(true), []);

  // Focus mode hides the only UI that can turn it back off (a page's own toggle icon) — reset
  // on navigation so leaving the page a user enabled it on can never strand them without chrome.
  useEffect(() => { setFocusMode(false); }, [location.pathname]);

  return (
    <PageFocusContext.Provider value={{ focusMode, toggleFocusMode, exitFocusMode, enterFocusMode }}>
      {children}
    </PageFocusContext.Provider>
  );
}

export const usePageFocusMode = () => useContext(PageFocusContext);

// Shared by every full-viewport page-takeover (WO detail, Add/Edit WO, Add/Edit Schedule, ...):
// captures whatever focusMode was BEFORE this takeover mounted and forced it on, then restores
// that captured value on unmount instead of hardcoding it back off. Without this, a user who'd
// manually expanded the page via PageFocusToggle before opening a takeover would find the page
// un-expanded again after closing it — exitFocusMode() alone always sets focusMode to false
// regardless of what it was before.
export function useTakeoverFocusMode() {
  const { focusMode, enterFocusMode, exitFocusMode } = usePageFocusMode();
  const wasOnRef = useRef(focusMode);

  useEffect(() => {
    wasOnRef.current = focusMode;
    enterFocusMode();
    return () => {
      if (wasOnRef.current) enterFocusMode(); else exitFocusMode();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
